import { AlertCircle, RefreshCw } from "lucide-react";
import { Tonal } from "@/components/ui/tonal";
import { StepsTimeline } from "@/components/walkthrough/steps-timeline";
import { cn } from "@/lib/utils";
import type { WalkthroughFinding } from "./workspace-types";

interface WorkspaceTraceTabProps {
  walkthroughSteps: Array<{
    id: string;
    stepNumber: number;
    title: string;
    personaReasoning: string | null;
    grounding: string | null;
    screenshotPath: string | null;
    pageUrl?: string | null;
    surface?: string | null;
  }>;
  findings: WalkthroughFinding[];
  walkthroughId: string;
  personaName?: string | null;
  projectId: string;
  isFailed: boolean;
  isRunning: boolean;
  highlightedStepNumber: number | null;
  onOpenFinding: (id: string) => void;
  onOpenZenMode?: (index: number) => void;
}

export function WorkspaceTraceTab({
  walkthroughSteps,
  findings,
  walkthroughId,
  personaName,
  projectId,
  isFailed,
  isRunning,
  highlightedStepNumber,
  onOpenFinding,
  onOpenZenMode,
}: WorkspaceTraceTabProps) {
  if (walkthroughSteps.length === 0) {
    return (
      <Tonal
        padding="lg"
        className="text-center rounded-lg border border-border/80 space-y-3"
      >
        <div
          className={cn(
            "mx-auto flex h-10 w-10 items-center justify-center rounded-full",
            isFailed
              ? "bg-destructive/10 text-destructive"
              : isRunning
                ? "bg-primary/10 text-primary-ink"
                : "bg-muted text-muted-foreground",
          )}
        >
          {isFailed ? (
            <AlertCircle className="h-5 w-5" aria-hidden="true" />
          ) : isRunning ? (
            <RefreshCw className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <AlertCircle className="h-5 w-5" aria-hidden="true" />
          )}
        </div>
        <div className="space-y-1 max-w-[48ch] mx-auto">
          <h3 className="text-base font-semibold text-foreground">
            {isFailed
              ? "No execution steps recorded"
              : isRunning
                ? "Waiting for execution steps…"
                : "No execution steps recorded"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {isFailed
              ? "The runner disconnected or encountered an error before executing any steps."
              : isRunning
                ? `Steps will appear here as ${personaName || "the persona"} interacts with the application.`
                : "No execution trace was captured for this walkthrough."}
          </p>
        </div>
      </Tonal>
    );
  }

  return (
    <StepsTimeline
      steps={walkthroughSteps}
      findings={findings}
      walkthroughId={walkthroughId}
      personaName={personaName}
      projectId={projectId}
      onSelectFinding={onOpenFinding}
      highlightedStepNumber={highlightedStepNumber}
      onOpenZenMode={onOpenZenMode}
    />
  );
}
